import * as R from 'ramda';
import {
    contentFormatOptions,
    contentTierBaseOptions,
    socialChannelBaseOptions,
    contentUseBaseOptions,
    contentReuseBaseOptions,
    videoKindBaseOptions
} from '@social-native/snpkg-client-data-to-views';

import {
    DeliverableSocialPlatform,
    VideoProductionStyle,
    DistributionChannel,
    ContentInterfaceType,
    SocialPostKind,
    AspectRatio
} from '../../common/language';

export const parseUiRef = uiRefString => {
    try {
        return JSON.parse(uiRefString);
    } catch (error) {
        return null;
    }
};

/** Access the nested node object returned in the edges
 * property when making a GraphQL query for array items */
export const getEdgeNode = edge => edge && edge.node;

/** Access the unflattened edges property returned by a plural */
export const getConnectionEdges = plural => (plural && plural.edges) || null;

/** Access the flattened array of edge nodes from the edges level */
export const getEdgeNodes = edges => (edges && edges.map(getEdgeNode)) || null;

/** Access the flattened array of edge nodes from the plural level */
export const getConnectionNodes = plural => getEdgeNodes(getConnectionEdges(plural));

/**
 * @param {ProductionNode} productionNode
 */
export const parseDirectionNodesFromProdNode = productionNode =>
    productionNode.tree.nodes.reduce(
        (result, productionTreeNode) => {
            const productionDirectionTree = productionTreeNode.directions;
            if (productionDirectionTree) {
                result.directionTreeNodes.byUiRefName = productionDirectionTree.nodes.reduce(
                    (res, node) => {
                        if (node.uiRef) {
                            const parsedUiRef = parseUiRef(node.uiRef);
                            res[parsedUiRef.name] = [...(res[parsedUiRef.name] || []), node];
                        }
                        return res;
                    },
                    result.directionTreeNodes.byUiRefName
                );
            }

            return result;
        },
        {
            directionTreeNodes: {
                byUiRefName: {}
            }
        }
    );

/**
 * @param {ProductionNode} productionNode
 * @return {*}
 */
export const parseConquestingBrands = productionNode => {
    if (!(productionNode.conquestBrandNames && productionNode.conquestBrandNames.edges.length)) {
        return null;
    }

    const nodes = getConnectionNodes(productionNode.conquestBrandNames);
    const sorted = nodes.reduce(
        (result, node) => {
            const item = {
                ...node,
                label: node.name,
                value: node.name
            };
            if (node.conquestKind === 'INCLUDE_CREATORS') {
                result.include.items.push(item);
            }
            if (node.conquestKind === 'EXCLUDE_CREATORS') {
                result.exclude.items.push(item);
            }

            result.byBrandNameId[item.name] = node;
            return result;
        },
        {
            byBrandNameId: {},
            exclude: {
                items: []
            },
            include: {
                items: []
            }
        }
    );
    return sorted;
};

const getDescriptor = (directions, direction) => {
    if (directions[direction] && directions[direction].length > 0) {
        return directions[direction][0].descriptor;
    }

    return null;
};

const getDescriptorArray = (directions, direction) => {
    if (directions[direction] && directions[direction].length > 0) {
        return directions[direction].map(d => d.descriptor);
    }

    return null;
};

const mapValue = (rawValue, options) => {
    if (rawValue) {
        return options.find(option => option.value === rawValue).label;
    }

    return null;
};

const mapArrayValue = (rawValuesArray, options) => {
    if (rawValuesArray && rawValuesArray.length > 0) {
        const mappedArray = rawValuesArray.map(
            value => options.find(option => option.value === value).label
        );
        return mappedArray.join(', ');
    }

    return null;
};

const mapSizeValue = (rawFormatValue, rawSizeValue, options) => {
    if (rawFormatValue && rawSizeValue) {
        const sizeOptions = options.find(option => option.value === rawFormatValue).sizes;
        if (!sizeOptions) {
            return null;
        }
        return mapValue(rawSizeValue, sizeOptions);
    }

    return null;
};

const cleanLocationValues = locations => {
    if (locations) {
        const locationsAll = locations.map(location => location.split('|')[0]);
        return locationsAll.join('| ');
    }
    return null;
};

export const generateEmptyGuideline = (operatorKey = 'MUST', sortIndex = -1) => ({
    operator: operatorKey,
    descriptor: '',
    sortIndex
});

const parseGuidelines = nodes => {
    if (!nodes) {
        return {
            fieldValue: {
                statusNodes: {},
                createInput: generateEmptyGuideline('MUST'),
                created: []
            }
        };
    }
    const {guidelines, statusNodes} = nodes.reduce(
        (result, node, index) => {
            const parsedUiRef = parseUiRef(node.uiRef);
            if (parsedUiRef.isStatusNode && parsedUiRef.status) {
                result.statusNodes[node.descriptor] = {
                    node,
                    parsedUiRef,
                    key: `${index}-${node.descriptor}`
                };
                return result;
            }
            result.guidelines.push({
                node,
                guideline: {
                    operator: node.operator === 'NOT_EQUAL' ? 'MUST_NOT' : 'MUST',
                    descriptor: node.descriptor,
                    nodeId: node.id,
                    parsedUiRef,
                    sortIndex: parsedUiRef.sort,
                    isModified: parsedUiRef.isModified,
                    directionNode: node
                }
            });
            return result;
        },
        {
            statusNodes: {},
            guidelines: []
        }
    );

    return {
        fieldValue: {
            statusNodes,
            createInput: generateEmptyGuideline('MUST'),
            created:
                guidelines.length &&
                R.sortBy(R.path(['guideline', 'sortIndex']), guidelines).map(node => node.guideline)
        }
    };
};

export const transformGen3CampaignForVerifyReceipt = campaignData => {
    const transformData = {
        contentType: null,
        videoType: null,
        socialChannel: null,
        contentFormat: null,
        contentSize: null,
        contentUse: null
    };

    const deliverableDescriptor = campaignData.deliverableDescriptors.edges[0].node;

    if (deliverableDescriptor.contentSpecs?.edges.length) {
        const contentSpec = deliverableDescriptor.contentSpecs.edges[0].node;
        transformData.contentType = ContentInterfaceType[contentSpec.kind];

        if (contentSpec.aspectRatioOptions?.length) {
            transformData.contentSize = AspectRatio[contentSpec.aspectRatioOptions[0]];
        }

        if (contentSpec.productionStyleOptions && contentSpec.productionStyleOptions.length) {
            transformData.videoType = VideoProductionStyle[contentSpec.productionStyleOptions[0]];
        }
    }

    if (deliverableDescriptor.socialPlatform) {
        transformData.socialChannel =
            DeliverableSocialPlatform[deliverableDescriptor.socialPlatform];
    }

    if (deliverableDescriptor.intendedForDistributionChannels?.length) {
        transformData.contentUse =
            DistributionChannel[deliverableDescriptor.intendedForDistributionChannels[0].kind];
    } else {
        transformData.contentUse = DistributionChannel['SOCIAL'];
    }

    if (deliverableDescriptor.socialPostKind) {
        transformData.contentFormat = SocialPostKind[deliverableDescriptor.socialPostKind];
    } else if (deliverableDescriptor.intendedForDistributionChannels?.length) {
        deliverableDescriptor.intendedForDistributionChannels.forEach(intended => {
            if (!transformData.contentFormat && intended.socialPostKinds?.length) {
                transformData.contentFormat = SocialPostKind[intended.socialPostKinds[0]];
            }
        });
    }

    return transformData;
};

export const transformCampaignForVerifyReceipt = campaignData => {
    const productionNode = campaignData.productions.edges[0].node;
    const parsedDirectionNodes = parseDirectionNodesFromProdNode(productionNode);

    const directions = parsedDirectionNodes.directionTreeNodes.byUiRefName;

    const contentType = productionNode.finalDeliverable && productionNode.finalDeliverable.kind;

    const contentTier = mapValue(getDescriptor(directions, 'CONTENT_TIER'), contentTierBaseOptions);

    const socialChannel = mapValue(
        getDescriptor(directions, 'SOCIAL_CHANNEL'),
        socialChannelBaseOptions
    );

    const videoType = mapValue(getDescriptor(directions, 'VIDEO_TYPE'), videoKindBaseOptions);

    const contentUse = mapValue(getDescriptor(directions, 'CONTENT_USAGE'), contentUseBaseOptions);

    const contentReuse = mapArrayValue(
        getDescriptorArray(directions, 'CONTENT_REUSE'),
        contentReuseBaseOptions
    );

    const contentFormat = mapValue(
        getDescriptor(directions, 'CONTENT_FORMAT'),
        contentFormatOptions
    );

    const contentSize = mapSizeValue(
        getDescriptor(directions, 'CONTENT_FORMAT'),
        getDescriptor(directions, 'CONTENT_SIZE'),
        contentFormatOptions
    );

    const customLocations = cleanLocationValues(getDescriptorArray(directions, 'CUSTOM_LOCATIONS'));

    const conquestingBrands = parseConquestingBrands(productionNode);

    const parsedContentGuidelines = parseGuidelines(directions.CONTENT_GUIDELINES);

    const contentGuidelines = generateParsedGuidelines(parsedContentGuidelines.fieldValue.created);

    const parsedCaptionGuidelines = parseGuidelines(directions.CAPTION_GUIDELINES);

    const captionGuidelines = generateParsedGuidelines(parsedCaptionGuidelines.fieldValue.created);

    return {
        contentType,
        videoType,
        contentTier,
        socialChannel,
        contentFormat,
        contentSize,
        contentUse,
        contentReuse,
        customLocations,
        conquestingBrands,
        contentGuidelines,
        captionGuidelines
    };
};

const generateParsedGuidelines = guidelines => {
    if (!guidelines) {
        return [];
    }

    return guidelines.map(guideline => ({
        guideline:
            guideline.operator === 'MUST'
                ? `Must ${guideline.descriptor}`
                : `Must not ${guideline.descriptor}`,
        should: guideline.operator === 'MUST' ? 1 : 0
    }));
};

const generateHashtagAndHandleGuidelines = (typeOfCaption, symbol, guidelines) => {
    if (!guidelines) {
        return [];
    }

    const guidelinesMap = guidelines.map(guideline => `${symbol}${guideline.descriptor}`);

    return [
        {
            guideline: `Must include these ${typeOfCaption}:  ${guidelinesMap.join(', ')}`,
            should: 1
        }
    ];
};

export const transformCampaignForGuidelines = (campaignData, appendHashtagsAndHandles = true) => {
    const productionNode = campaignData.productions.edges[0].node;
    const parsedDirectionNodes = parseDirectionNodesFromProdNode(productionNode);
    const directions = parsedDirectionNodes.directionTreeNodes.byUiRefName;

    const parsedContentGuidelines = parseGuidelines(directions.CONTENT_GUIDELINES);

    const contentGuidelines = generateParsedGuidelines(parsedContentGuidelines.fieldValue.created);

    const parsedCaptionGuidelines = parseGuidelines(directions.CAPTION_GUIDELINES);

    const captionGuidelines = generateParsedGuidelines(parsedCaptionGuidelines.fieldValue.created);

    const hashtagsGuidelines = appendHashtagsAndHandles
        ? generateHashtagAndHandleGuidelines('hashtags', '#', directions.HASHTAGS)
        : [];
    const handlesGuidelines = appendHashtagsAndHandles
        ? generateHashtagAndHandleGuidelines('handles', '@', directions.HANDLES)
        : [];

    return {
        contentGuidelines,
        captionGuidelines: [...captionGuidelines, ...hashtagsGuidelines, ...handlesGuidelines]
    };
};



// WEBPACK FOOTER //
// ./graphql/transforms/campaign.js